import * as React from "react";
import { useState } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Alert } from "components/bootstrap";
import { Icon, PaginatedList, Spinner } from "components/common";
import Routes from "routing/Routes";
import useHistory from "routing/useHistory";
import { fetchPaginatedInvestigationLogs } from "security-app/hooks/api/investigationsAPI";
import { investigationDrawerLogsQueryKey } from "security-app/hooks/queryKeys";
import i18n from "i18n";
import { defaultOnError } from "util/conditional/onError";
import { EvidenceList, EvidenceRow, IconButton, StyledLink } from "../styled.components";
const DEFAULT_LOGS_PAGE_SIZE = 10;
const useInvestigationLogs = (investigationId, page) => {
  var _a, _b, _c;
  const {
    data: paginatedLogs,
    isLoading,
    isError,
    error
  } = useQuery({
    queryKey: investigationDrawerLogsQueryKey(investigationId, page, DEFAULT_LOGS_PAGE_SIZE),
    queryFn: () => defaultOnError(
      fetchPaginatedInvestigationLogs(investigationId, page, DEFAULT_LOGS_PAGE_SIZE),
      i18n.t("notifications.fetch_investigation_logs_failed", { ns: "enterprise_security_investigations" })
    ),
    enabled: !!investigationId,
    placeholderData: keepPreviousData
  });
  return {
    hasLoadedLogs: !!paginatedLogs,
    isError,
    isLoading,
    logs: ((_a = paginatedLogs == null ? void 0 : paginatedLogs.elements) != null ? _a : []).map(
      (log, listIndex) => {
        var _a2, _b2, _c2;
        return {
          id: String((_b2 = (_a2 = log.message) == null ? void 0 : _a2._id) != null ? _b2 : `investigation-log-${page}-${listIndex}`),
          index: log.index,
          timestamp: (_c2 = log.message) == null ? void 0 : _c2.timestamp
        };
      }
    ),
    totalLogs: (_b = paginatedLogs == null ? void 0 : paginatedLogs.pagination) == null ? void 0 : _b.total,
    errorMessage: (_c = error == null ? void 0 : error.message) != null ? _c : "Failed to load logs."
  };
};
const LogsList = ({ investigation, canManageEvidence, onDelete }) => {
  const { t } = useTranslation("enterprise_security_investigations");
  const history = useHistory();
  const [page, setPage] = useState(1);
  const {
    logs,
    totalLogs,
    isLoading: loadingLogs,
    isError: isLogsError,
    errorMessage,
    hasLoadedLogs
  } = useInvestigationLogs(investigation == null ? void 0 : investigation.id, page);
  if (loadingLogs && !hasLoadedLogs) {
    return /* @__PURE__ */ React.createElement(Spinner, { text: t("drawer_logs_list.loading") });
  }
  if (isLogsError && !hasLoadedLogs) {
    return /* @__PURE__ */ React.createElement(Alert, { bsStyle: "danger" }, errorMessage);
  }
  if (totalLogs === 0) {
    return /* @__PURE__ */ React.createElement("div", { style: { color: "gray" } }, t("drawer_logs_list.empty"));
  }
  return /* @__PURE__ */ React.createElement(React.Fragment, null, isLogsError && hasLoadedLogs && /* @__PURE__ */ React.createElement(Alert, { bsStyle: "danger" }, errorMessage), /* @__PURE__ */ React.createElement(
    PaginatedList,
    {
      activePage: page,
      pageSize: DEFAULT_LOGS_PAGE_SIZE,
      onChange: setPage,
      totalItems: totalLogs,
      showPageSizeSelect: false,
      hideFirstAndLastPageLinks: true,
      useQueryParameter: false
    },
    /* @__PURE__ */ React.createElement(EvidenceList, null, logs.map((log) => /* @__PURE__ */ React.createElement(EvidenceRow, { key: log.id, $fullWidth: true, $gap: "0" }, canManageEvidence && /* @__PURE__ */ React.createElement(IconButton, { $danger: true, "data-testid": `delete-${log.id}`, onClick: onDelete(log) }, /* @__PURE__ */ React.createElement(Icon, { name: "close" })), /* @__PURE__ */ React.createElement(StyledLink, { onClick: () => history.push(Routes.message_show(log.index, log.id)) }, log.id, " ", /* @__PURE__ */ React.createElement("span", { style: { color: "gray" } }, "(", log.index, ")")))))
  ));
};
export default LogsList;
